import React, {useRef, useState} from "react";
import {Accordion, Button, Col, Form, Row} from "react-bootstrap";
import {RequireAuth} from "Components/Security/RequireAuth";
import {ROUTE_PATHS} from "Config/Router/Routes";
import {exportMonthlyFeeModifications, getMonthlyFeeModifications} from "Api/Query/MonthlyFeeModificationQuery";
import {AdminCrudList} from "Views/Shared/Crud/AdminCrudList";
import {
    deleteMonthlyFeeModifications,
    uploadMonthlyFeeModificationsFile
} from "Api/Mutation/MonthlyFeeModificationMutation";
import {MoneyAmount} from "Components/Money/MoneyAmount";
import {FileUpload} from "../../Components/FileUpload/FileUpload";
import {Notificator} from "../../Services/Notificator/Notificator";
import {useMutation, useQuery} from "react-query";
import {appLoading} from "../../Store/loadingSlice";
import {ErrorMessageBuilder} from "../../Services/Notificator/ErrorMessageBuilder";
import {useAppDispatch} from "../../hooks";
import {RefreshSpinIcon} from "../../Components/Icons/RefreshSpinIcon";
import {forceDownloadFromUrl} from "../../Services/File/FileDownloader";
import {CenterSelect} from "../../Components/Center/CenterSelect";

export const MonthlyFeeModificationList : React.FC = () => {

    const ALLOWED_SEPA_XML_FILETYPES = [
        "application/vnd.ms-excel",
        "application/vnd.ms-excel.sheet.macroEnabled.12",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ]

    const dispatch = useAppDispatch()
    const [errors, setErrors] = useState<string>('')
    const [dropzoneFiles, setDropzoneFiles] = useState<any>([])
    const [exporting, setExporting] = useState<boolean>(false)
    const page = useRef<number>(1)

    const [centerFilter, setCenterFilter] = useState<any>(null)
    const [customFiltersData, setCustomFiltersData] = useState<{ [key: string]: any }>({})
    const [customFiltersDataToApply, setCustomFiltersDataToApply] = useState<{ [key: string]: any }>(customFiltersData)

    const uploadMutation = useMutation({
        mutationFn: (body: any) => uploadMonthlyFeeModificationsFile(body.file),
        onMutate: (body: any) => {
            dispatch(
                appLoading({
                    value: true,
                    text: `Processando archivo: ${body.file.name} ...`
                })
            )
        },
        onError: (error: any) => {
            dispatch(appLoading({value: false}))

            const notificationMessage = ErrorMessageBuilder.create(error)
            Notificator.error(notificationMessage, 'Error')
        },
        onSuccess: (response: any) => {
            setDropzoneFiles([])
            dispatch(appLoading({value: false}))

            const data = response.data?._result ?? [];

            if(data.errors.length){
                setErrors(data.errors.join("\n"))
                Notificator.warning('Los datos se han importado con errores.')
            }else{
                Notificator.success('Los gastos se han importado correctamente')
            }
        }
    })

    const headers: string[] = [
        'AÑO',
        'MES',
        'NIÑO',
        'CENTRO',
        'IMPORTE',
        'OBSERVACIONES',
    ]

    const monthNames = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    const exportQuery = useQuery({
        enabled: false,
        queryKey: ['exportMonthlyFeeModifications', customFiltersDataToApply],
        queryFn: () => exportMonthlyFeeModifications(customFiltersDataToApply),
        onSuccess: (response: any) => {
            const responseData = response.data
            forceDownloadFromUrl(responseData._result.url);
            setExporting(false)
            dispatch(appLoading({value: false, text: 'Cargando'}))
        },
        onError: () => {
            Notificator.error('Se ha producido un error inesperado.')
            setExporting(false)
            dispatch(appLoading({value: false, text: 'Cargando'}))
        }
    })

    const excelExport = () => {
        setExporting(true)
        exportQuery.refetch()
    }

    const applyFilterChanges = () => {
        let parsedFilters: any = {}

        if(customFiltersData.q){
            parsedFilters = {
                ...parsedFilters,
                q:customFiltersData.q,
            }
        }

        if(customFiltersData.center)
        {
            parsedFilters = {
                ...parsedFilters,
                center:customFiltersData.center,
            }
        }

        page.current = 1
        parsedFilters = {...parsedFilters, page: page.current}
        setCustomFiltersDataToApply(parsedFilters)
    }

    return (
        <RequireAuth>
            <AdminCrudList
                id="monthly-fee-modification-list"
                refreshSwitch={uploadMutation.isSuccess}
                title={"Listado de modificaciones de cuota mensual"}
                headers={headers}
                pageSize={12}
                queryMethod={getMonthlyFeeModifications}
                queryName={"adminListMonthlyFeeModifications"}
                deleteMethod={deleteMonthlyFeeModifications}
                export={
                    <Button
                        className={"purple-btn mx-4"}
                        onClick={excelExport}
                        disabled={exporting}>
                        {exporting && <><RefreshSpinIcon/>&nbsp;&nbsp;</>}
                        <>Exportar</>
                    </Button>
                }
                tableRow={(item: any, key: number) =>
                    <>
                        <td>{item.year}</td>
                        <td>{monthNames[item.monthNumber - 1] || item.monthNumber}</td>
                        <td>
                            <a href={ROUTE_PATHS.ADMIN_KIDS_EDIT.replace(":id", item.child?.id)} target="_blank">
                            {item.child?.name}
                            </a>
                        </td>
                        <td>
                            {item.center.name}
                        </td>
                        <td>
                            <MoneyAmount amount={item.amount}/>
                        </td>
                        <td>
                            {item.description}
                        </td>
                        <td className={"text-center align-middle"}>
                            <Button
                                className={"purple-btn btn-sm"}
                                href={ROUTE_PATHS.ADMIN_MONTHLY_FEE_MODIFICATION_EDIT.replace(":id", item.id)}
                            >
                                Modificar
                            </Button>
                        </td>
                    </>
                }
                customFiltersData={customFiltersDataToApply}
                customFilters={
                    <>
                        <Col xs={12} sm={6}>
                            <Form.Group controlId={"queryFilter"}>
                                <Form.Label>
                                    Buscar
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    onChange={(e: any) => {
                                        const value = e.target.value ?? ''
                                        setCustomFiltersData({...customFiltersData, q: value})
                                    }}
                                    value={customFiltersData.q ?? ''}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Group controlId={"queryFilter"}>
                                <Form.Label>
                                    Centro
                                </Form.Label>
                                <CenterSelect
                                    value={centerFilter}
                                    onChange={(option: any) => {
                                        setCenterFilter(option)

                                        if (option && option.value) {
                                            setCustomFiltersData({...customFiltersData, center: option.value})
                                        } else {
                                            let newFilters: any = {...customFiltersData}
                                            delete newFilters.center;
                                            setCustomFiltersData(newFilters)
                                        }
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Row className={"mt-2"}>
                            <Col>
                                <Button
                                    className={"purple-btn"}
                                    onClick={applyFilterChanges}
                                >Buscar</Button>
                                &nbsp;
                                <Button
                                    variant={"secondary"}
                                    onClick={() => {
                                        page.current = 1
                                        setCenterFilter(null)
                                        setCustomFiltersData({})
                                        setCustomFiltersDataToApply({})
                                    }
                                    }
                                >Limpiar filtros</Button>
                            </Col>
                        </Row>
                    </>
                }
                createButton={
                    <Button
                        className={"purple-btn"}
                        href={ROUTE_PATHS.ADMIN_MONTHLY_FEE_MODIFICATION_ADD}
                    >
                        Crear modificación de cuota mensual
                    </Button>
                }
                preTableChildren={
                    <Accordion>
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                Importar modificaciones de cuota mensual desde Excel
                            </Accordion.Header>
                            <Accordion.Body>
                                Estructura del excel:
                                <table className="table table-bordered mt-3">
                                    <thead>
                                    <tr>
                                        <th>Año (Columna 1)</th>
                                        <th>Mes (Columna 2)</th>
                                        <th>Niño (Columna 3)</th>
                                        <th>Importe (Columna 4)</th>
                                        <th>Observaciones (Columna 5)</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr>
                                        <td>2025</td>
                                        <td>5</td>
                                        <td>Apellidos, Nombre</td>
                                        <td>50.00</td>
                                        <td>Observaciones de prueba</td>
                                    </tr>
                                    <tr>
                                        <td>2025</td>
                                        <td>10</td>
                                        <td>Aparicio Muñoz, Miguel</td>
                                        <td>-20.00</td>
                                        <td>Observaciones de prueba</td>
                                    </tr>
                                    </tbody>
                                </table>
                                <br />
                                <FileUpload
                                    defaultFiles={dropzoneFiles}
                                    onChange={(files) => setDropzoneFiles(files)}
                                    acceptedMimmeTypes={ALLOWED_SEPA_XML_FILETYPES.join(",")}
                                    allowedFilesText={"Solo se admite el archivo excel de la plantilla con los datos financieros."}
                                    maxFiles={1}
                                />
                                <Button
                                    className={"purple-btn btn btn-primary"}
                                    disabled={dropzoneFiles.length === 0}
                                    onClick={() => {
                                        const files = dropzoneFiles

                                        if (typeof files[0] === 'undefined') {
                                            return
                                        }

                                        const file = files[0]

                                        if (!file.valid) {
                                            Notificator.error('Archivo inválido')
                                            return
                                        }

                                        uploadMutation.mutate({
                                            file: file.file,
                                        })
                                    }}
                                >Importar</Button>

                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                }
            />

        </RequireAuth>
    )
}

